import { useContext } from "react";
import { PharmacyDetails } from "capitalrx-components-2.0";
import { QueryParams } from "@/api/interfaces/QueryParams";
import PharmacyDetailsPriceCard from "../PharmacyDetailsPriceCard/PharmacyDetailsPriceCard";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";
import { ThemeSelectorContext } from "@/context/index";
import { LoadingSkeleton } from "capitalrx-components-2.0/lib/general/LoadingSkeleton/LoadingSkeleton";
import { Icon } from "capitalrx-components-2.0/lib/general";
import { Paragraph } from "capitalrx-components-2.0/lib/typography";
import { Grid, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";

import styles from "./PharmacyDetails.module.scss";

interface Pharmacy {
  pharmacyParams: QueryParams;
  pharmacyInfo: PharmacyDetails;
  isLoading: boolean;
}

const PharmacyDetailsComponent = (props: Pharmacy): JSX.Element => {
  const { pharmacyParams, pharmacyInfo, isLoading } = props;
  const { columns643, columns653 } = useResponsive({
    columns643: {
      desktop: 6,
      tablet: 4,
      mobile: 6,
    },
    columns653: {
      desktop: 6,
      tablet: 5,
      mobile: 6,
    },
  });

  const formattedPhone = formatPhoneNumber(pharmacyInfo?.phone);

  const { client } = useContext(ThemeSelectorContext);
  const iconColor = client ? "lightgrey" : "turquoise";

  const PharmacyAddress = () => {
    return (
      <div data-testid="pharmacy-details-address">
        {pharmacyInfo?.address_line_1} <br />
        {pharmacyInfo?.address_line_2 ? (
          <>
            {pharmacyInfo.address_line_2}
            <br />
          </>
        ) : null}
        {pharmacyInfo?.city}, {pharmacyInfo?.state} {pharmacyInfo?.zip}
      </div>
    );
  };

  const PharmacyLanguages = (): JSX.Element => {
    if (pharmacyInfo?.languages.length) {
      return (
        <div className={styles.PharmacyDetailsInfoFact} data-testid="pharmacy-details-languages">
          <Icon name="message-dots" color={iconColor} />
          <div>
            {pharmacyInfo?.languages.map((language) => {
              return <span key={language}>{language}</span>;
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  const PharmacyHours = (): JSX.Element => {
    return (
      <div data-testid="pharmacy-details-hours">
        {pharmacyInfo?.hours.map((day, index) => {
          return <span key={index}>{day}</span>;
        })}
      </div>
    );
  };

  const showPharmacyDetails = !isLoading && (
    <>
      <PharmacyDetailsPriceCard price={pharmacyParams?.price} />
      <div className={styles.PharmacyDetailsInfo}>
        <Paragraph className={styles.PharmacyDetailsInfoName} testId="pharmacy-details-pharmacy-name">
          {pharmacyInfo?.name}
        </Paragraph>
        <Grid testId="pharmacy-details-grid">
          <Grid.Spread columns={columns643}>
            <Stack>
              <div className={styles.PharmacyDetailsInfoFact}>
                <Icon name="location" color={iconColor} testId="pharmacy-details-address-icon" />
                <div>
                  <PharmacyAddress />
                </div>
              </div>
              <div className={styles.PharmacyDetailsInfoFact}>
                <Icon name="phone" color={iconColor} testId="pharmacy-details-phone-icon" />
                <div>{formattedPhone}</div>
              </div>

              <PharmacyLanguages />
            </Stack>

          </Grid.Spread>
          <Grid.Span columns={columns653}>
            <div className={styles.PharmacyDetailsInfoFact}>
              <Icon name="clock" color={iconColor} testId="pharmacy-details-hours-icon" />
              <PharmacyHours />
            </div>
          </Grid.Span>
        </Grid>
      </div>
    </>
  );

  const loadingSkeleton = isLoading && <LoadingSkeleton className={styles.LoadingSkeleton} count={6} />;

  return (
    <div className={styles.PharmacyDetails}>
      <div className={styles.PharmacyDetailsHeader}>
        <Paragraph className={styles.PharmacyDetailsHeaderName} testId="pharmacy-details-header-pharmacy-name">
          {pharmacyInfo?.name}
        </Paragraph>
        <Paragraph className={styles.PharmacyDetailsHeaderDistance} testId="pharmacy-details-header-distance">
          {pharmacyParams?.distance} miles
        </Paragraph>
      </div>
      {showPharmacyDetails}
      {loadingSkeleton}
    </div>
  );
};

export default PharmacyDetailsComponent;
